import React from 'react';
import {
  ListItem,
  ListItemText,
  Box,
  Typography,
  Chip,
  IconButton,
  Checkbox
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Individual grocery list item component
 * 
 * @param {Object} props
 * @param {Object} props.item - Grocery item data
 * @param {number} props.index - Item index
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onDelete - Delete handler
 * @param {boolean} props.editable - Whether item can be edited/deleted
 * @param {boolean} props.showActions - Whether to show action buttons
 * @param {Function} props.onTogglePurchased - Toggle purchased status (optional)
 */
const GroceryListItem = ({
  item,
  index,
  onEdit,
  onDelete,
  editable = true,
  showActions = true,
  onTogglePurchased
}) => {
  const { t } = useTranslation();

  const handleEdit = () => {
    if (onEdit) {
      onEdit(item, index);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(index);
    }
  };

  const handleTogglePurchased = (event) => {
    if (onTogglePurchased) {
      onTogglePurchased(item.id, event.target.checked);
    }
  };

  return (
    <ListItem
      divider
      sx={{
        opacity: item.is_purchased ? 0.6 : 1,
        textDecoration: item.is_purchased ? 'line-through' : 'none'
      }}
      secondaryAction={
        showActions && editable ? (
          <Box>
            <IconButton 
              edge="end" 
              onClick={handleEdit} 
              sx={{ mr: 1 }}
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton 
              edge="end" 
              onClick={handleDelete} 
              color="error"
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null
      }
    >
      {onTogglePurchased && item.id && (
        <Checkbox
          checked={item.is_purchased || false}
          onChange={handleTogglePurchased}
          sx={{ mr: 1 }}
        />
      )}
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography 
              variant="body1" 
              fontWeight="medium"
              sx={{ 
                textDecoration: item.is_purchased ? 'line-through' : 'none'
              }}
            >
              {item.ingredient_name || item.name}
            </Typography>
            {item.category && (
              <Chip 
                label={item.category} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
          </Box>
        }
        secondary={
          <Box component="span" display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="body2" color="text.secondary">
              {item.quantity || 
               (item.total_amount 
                 ? `${item.total_amount} ${item.measure_unit || ''}` 
                 : t('shopping.asNeeded', 'As needed'))}
            </Typography>
            {item.notes && (
              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                {t('shopping.note', 'Note')}: {item.notes}
              </Typography>
            )}
            {item.shopping_date && (
              <Typography variant="caption" color="text.secondary">
                {t('shopping.date', 'Date')}: {item.shopping_date}
              </Typography>
            )}
          </Box>
        }
      />
    </ListItem>
  );
};

export default GroceryListItem;
